import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * FilterPopover — per-column filter dropdown triggered from a header icon.
 * Shows unique values as checkboxes. Highlights when filter is active.
 *
 * The popover is rendered in a React portal with position: fixed so it floats
 * above the table rather than being clipped by its overflow. On open (and on
 * window resize/scroll), its coordinates are computed from the trigger's
 * bounding rect and clamped to stay within the viewport.
 *
 * @param {{
 *   columnKey: string,
 *   label: string,
 *   values: string[],
 *   selectedValues: string[],
 *   onChange: (columnKey: string, values: string[]) => void,
 *   wide?: boolean   // widen the popover + wrap option text (for long values
 *                    // like "Device Name (uuid)"); default false keeps the
 *                    // standard 14rem ellipsis layout used by most columns.
 * }} props
 */

// Fallback popover width used only for the first render before we measure
// the actual DOM node. Final positioning uses the measured width.
const FALLBACK_WIDTH = 224;
const POPOVER_MAX_HEIGHT = 360;
const VIEWPORT_MARGIN = 8;

const FilterPopover = ({ columnKey, label, values, selectedValues, onChange, wide = false }) => {
  const [open, setOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  // visible=false on first mount so the popover can measure itself without a flash
  // at the wrong position. Switched to true after the first layout pass.
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    maxHeight: POPOVER_MAX_HEIGHT,
    visible: false,
  });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const isActive = selectedValues.length > 0;

  // Compute popover position relative to the trigger, clamped to the viewport.
  // Uses the actual rendered popover size (when available) so clamping matches
  // whatever the CSS produces — no hardcoded width assumption.
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();

    // Prefer the visual viewport so we don't mis-clamp when the browser zooms
    // or a virtual keyboard is visible on mobile.
    const vw = window.visualViewport?.width ?? document.documentElement.clientWidth ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? document.documentElement.clientHeight ?? window.innerHeight;

    // Use the measured popover size when available; fall back on first pass.
    const popRect = popoverRef.current?.getBoundingClientRect();
    const popWidth = popRect?.width || FALLBACK_WIDTH;

    // 1. Center horizontally under the trigger.
    let left = rect.left + rect.width / 2 - popWidth / 2;
    // 2. Only shift when it would overflow: clamp to viewport edges.
    const maxLeft = vw - popWidth - VIEWPORT_MARGIN;
    if (left > maxLeft) left = maxLeft;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

    // Vertical: open below by default; flip above if below is too tight.
    const spaceBelow = vh - rect.bottom - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - VIEWPORT_MARGIN;
    let top;
    let maxHeight;
    if (spaceBelow >= Math.min(POPOVER_MAX_HEIGHT, 200) || spaceBelow >= spaceAbove) {
      top = rect.bottom + 4;
      maxHeight = Math.min(POPOVER_MAX_HEIGHT, spaceBelow);
    } else {
      maxHeight = Math.min(POPOVER_MAX_HEIGHT, spaceAbove);
      top = rect.top - 4 - maxHeight;
    }

    setPosition({ top, left, maxHeight, visible: true });
  }, []);

  // Run positioning in two passes when opening: once to get an initial placement
  // (using the fallback width), then again after the popover has rendered so we
  // can use the real measured width.
  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const raf = requestAnimationFrame(() => updatePosition());
    return () => cancelAnimationFrame(raf);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const closePopover = () => {
      setOpen(false);
      setFilterSearch('');
      setPosition((p) => ({ ...p, visible: false }));
    };
    const handleClickOutside = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        closePopover();
      }
    };
    const handleReposition = () => updatePosition();
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closePopover();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, updatePosition]);

  const handleToggle = useCallback(
    (value) => {
      const next = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onChange(columnKey, next);
    },
    [columnKey, selectedValues, onChange]
  );

  const handleClear = useCallback(() => {
    onChange(columnKey, []);
  }, [columnKey, onChange]);

  const handleSelectAll = useCallback(() => {
    onChange(columnKey, [...values]);
  }, [columnKey, values, onChange]);

  const filteredValues = values.filter((v) =>
    v.toLowerCase().includes(filterSearch.toLowerCase())
  );

  return (
    <div className="rp-filter">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={`rp-filter__trigger ${isActive ? 'is-active' : ''}`}
        aria-label={`Filter ${label}`}
        aria-expanded={open}
      >
        <i className="fe fe-filter" aria-hidden="true" />
      </button>

      {open && createPortal(
        <div
          ref={popoverRef}
          className="rp-filter__popover rp-filter__popover--floating"
          role="dialog"
          aria-label={`Filter options for ${label}`}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            right: 'auto',
            bottom: 'auto',
            transform: 'none',
            margin: 0,
            maxHeight: `${position.maxHeight}px`,
            // Flex layout so the scrollable list fills the remaining
            // vertical space after the search/actions headers.
            display: 'flex',
            flexDirection: 'column',
            // Elevate above drawers/modals that might share --z-popover.
            zIndex: 9999,
            visibility: position.visible ? 'visible' : 'hidden',
            // Wide variant: override the default 14rem so long values
            // (e.g. "Device Name (uuid)") aren't truncated.
            ...(wide ? { width: '22rem' } : {}),
          }}
        >
          {/* Search within filter values */}
          <div className="rp-filter__search">
            <input
              type="text"
              placeholder="Search..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="rp-filter__search-input"
              autoFocus
            />
          </div>

          {/* Select all / Clear */}
          <div className="rp-filter__actions">
            <button
              type="button"
              onClick={handleSelectAll}
              className="rp-filter__action-btn rp-filter__action-btn--select"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="rp-filter__action-btn rp-filter__action-btn--clear"
            >
              Clear
            </button>
          </div>

          {/* Checkbox list — flex-grow fills remaining space within the
              popover's dynamic max-height, overriding each page CSS's
              fixed max-height on .rp-filter__list. */}
          <div
            className="rp-filter__list"
            style={{ flex: '1 1 auto', maxHeight: 'none', minHeight: 0 }}
          >
            {filteredValues.length === 0 ? (
              <p className="rp-filter__empty">No values found</p>
            ) : (
              filteredValues.map((value) => {
                const displayValue = value || '(empty)';
                const isChecked = selectedValues.includes(value);
                return (
                  <label key={value} className="rp-filter__option">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggle(value)}
                      className="rp-checkbox"
                    />
                    <span
                      className="rp-filter__option-text"
                      title={displayValue}
                      style={wide ? { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' } : undefined}
                    >
                      {displayValue}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default FilterPopover;
